import React, { useState } from "react";
import * as Sentry from "@sentry/react";
import * as Yup from "yup";
import { Form, Formik } from "formik";
import styled from "styled-components";
import {
  Modal,
  ModalBackground,
  ModalContent,
  ModalClose,
  ModalHeading,
} from "./library/Modal";
import Button from "./library/Button";
import ButtonGroup from "./library/ButtonGroup";
import {
  FormError,
  SubmitButton,
  TextInputField,
  RadioFieldGroup,
  RadioField,
} from "./library/Form";
import PartnerTeamMemberRequest from "../apiResources/PartnerTeamMemberRequest";
import { useQueryClient } from "react-query";

type RequestPartnerTeamMemberModalProps = {
  isOpen: boolean;
  onDismiss: () => void;
  onSuccess: () => void;
};

type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  role: "member" | "admin";
};

const RequestPartnerTeamMemberModal = (
  props: RequestPartnerTeamMemberModalProps
) => {
  const { isOpen, onDismiss, onSuccess } = props;
  const [formError, setFormError] = useState("");
  const queryClient = useQueryClient();

  const submitRequest = async (values: FormValues) => {
    try {
      const request = new PartnerTeamMemberRequest({
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        admin: values.role === "admin",
      });

      if (await request.save()) {
        queryClient.invalidateQueries("teamMembers");
        queryClient.invalidateQueries("pendingPartnerTeamMemberRequests");
        onSuccess();
      } else {
        const errorMessage =
          request.errors?.email?.fullMessage ||
          request.errors?.base?.fullMessage ||
          "Could not submit your request. Check the details or try again.";
        setFormError(errorMessage);
      }
    } catch (err) {
      setFormError("Uh oh, something went wrong. Please try again.");
      Sentry.captureException(err);
    }
  };

  return (
    <Modal id="request-partner-member-modal" isOpen={isOpen} onDismiss={onDismiss}>
      <ModalBackground onClick={onDismiss} />
      <ModalContent>
        <ModalHeading>Request team member</ModalHeading>

        <Intro>
          Submit this form to ask your HiringHub account manager to add someone
          to your team. You will get an email when the request is approved or
          declined.
        </Intro>

        {formError && <FormError>{formError}</FormError>}

        <Formik<FormValues>
          initialValues={{
            firstName: "",
            lastName: "",
            email: "",
            role: "member",
          }}
          onSubmit={submitRequest}
          validationSchema={Yup.object({
            firstName: Yup.string().required("Please enter a first name"),
            lastName: Yup.string().required("Please enter a last name"),
            email: Yup.string()
              .email("Please enter an email")
              .required("Please enter an email"),
          })}
        >
          <SpacedForm>
            <FieldsLayout>
              <TextInputField name="firstName" label="First Name" />
              <TextInputField name="lastName" label="Last Name" />
              <TextInputField type="email" name="email" label="Email" />

              <RadioFieldGroup label="User Role" name="role">
                <RadioField name="role" value="member">
                  Member
                </RadioField>
                <RadioField name="role" value="admin">
                  Admin
                </RadioField>
              </RadioFieldGroup>
            </FieldsLayout>
            <ButtonGroup>
              <SubmitButton submittingText="Sending...">Submit request</SubmitButton>
              <Button tertiary onClick={onDismiss}>
                Cancel
              </Button>
            </ButtonGroup>
          </SpacedForm>
        </Formik>

        <ModalClose onClick={onDismiss} aria-label="Close modal" />
      </ModalContent>
    </Modal>
  );
};

const Intro = styled.p`
  margin: 1rem 0 0;
  line-height: 1.5;
  color: #444;
`;

const SpacedForm = styled(Form)`
  margin-top: 2rem;
`;

const FieldsLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 1.5rem;

  @media only screen and (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export default RequestPartnerTeamMemberModal;
